import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Plus,
  Search,
  Filter,
  Calendar,
  Trash2,
  Edit,
  X,
  GripVertical,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const Operaciones = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    status: "backlog",
    due_date: "",
    tags: [],
  });

  const columns = [
    { id: "backlog", title: "Backlog", color: "#94A3B8" },
    { id: "todo", title: "Por Hacer", color: "#6C8ECC" },
    { id: "in_progress", title: "En Progreso", color: "#F59E0B" },
    { id: "review", title: "Revisión", color: "#8B5CF6" },
    { id: "done", title: "Completado", color: "#10B981" },
  ];

  const priorities = {
    low: { label: "Baja", color: "bg-slate-100 text-slate-600" },
    medium: { label: "Media", color: "bg-amber-100 text-amber-700" },
    high: { label: "Alta", color: "bg-orange-100 text-orange-700" },
    urgent: { label: "Urgente", color: "bg-rose-100 text-rose-700" },
  };

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?module=operaciones`, {
        withCredentials: true,
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      // Initialize with empty array for production
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newTasks = [...tasks];
    const taskIndex = newTasks.findIndex((t) => t.task_id === draggableId);
    if (taskIndex > -1) {
      newTasks[taskIndex] = {
        ...newTasks[taskIndex],
        status: destination.droppableId,
      };
      setTasks(newTasks);

      try {
        await axios.put(
          `${API}/tasks/${draggableId}`,
          { status: destination.droppableId },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error updating task:", error);
      }
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/tasks`,
        { ...newTask, module: "operaciones" },
        { withCredentials: true }
      );
      setTasks([...tasks, response.data]);
    } catch (error) {
      console.error("Error creating task:", error);
      // Add locally for demo
      const mockTask = {
        task_id: `task_${Date.now()}`,
        ...newTask,
        module: "operaciones",
        created_by: "user1",
        created_at: new Date().toISOString(),
      };
      setTasks([...tasks, mockTask]);
    }
    
    setShowModal(false);
    setEditingTask(null);
    setNewTask({
      title: "",
      description: "",
      priority: "medium",
      status: "backlog",
      due_date: "",
      tags: [],
    });
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || "",
      priority: task.priority,
      status: task.status,
      due_date: task.due_date || "",
      tags: task.tags || [],
    });
    setShowModal(true);
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    
    try {
      await axios.put(
        `${API}/tasks/${editingTask.task_id}`,
        newTask,
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error updating task:", error);
    }
    
    // Update locally
    setTasks(tasks.map(t => 
      t.task_id === editingTask.task_id 
        ? { ...t, ...newTask }
        : t
    ));
    
    setShowModal(false);
    setEditingTask(null);
    setNewTask({
      title: "",
      description: "",
      priority: "medium",
      status: "backlog",
      due_date: "",
      tags: [],
    });
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta tarea?")) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting task:", error);
    }
    
    setTasks(tasks.filter((t) => t.task_id !== taskId));
  };

  const getTasksByStatus = (status) => {
    return tasks.filter((t) => {
      const matchesStatus = t.status === status;
      const matchesSearch = !searchQuery || 
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operaciones">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Operaciones</h1>
          <p className="text-sm sm:text-base text-slate-500">Gestión de tareas y cronogramas operativos</p>
        </div>
        <button
          onClick={() => {
            setEditingTask(null);
            setNewTask({
              title: "",
              description: "",
              priority: "medium",
              status: "backlog",
              due_date: "",
              tags: [],
            });
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
          data-testid="create-task-btn"
        >
          <Plus size={20} />
          Nueva Tarea
        </button>
      </div>

      {/* Filters */}
      <div className="glass-panel p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar tareas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full glass-input pl-10"
            data-testid="search-tasks-input"
          />
        </div>
        <button className="btn-secondary flex items-center gap-2 justify-center">
          <Filter size={18} />
          Filtrar
        </button>
      </div>

      {/* Kanban Board - Vertical Layout */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-6">
          {columns.map((column) => (
            <div key={column.id} className="glass-panel p-4 sm:p-6">
              {/* Column Header */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: column.color }}
                ></div>
                <h3 className="font-semibold text-slate-700">{column.title}</h3>
                <span className="text-sm text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                  {getTasksByStatus(column.id).length}
                </span>
              </div>

              {/* Tasks */}
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[100px] rounded-xl transition-colors ${
                      snapshot.isDraggingOver ? "bg-primary/5" : ""
                    }`}
                    data-testid={`column-${column.id}`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {getTasksByStatus(column.id).map((task, index) => (
                        <Draggable
                          key={task.task_id}
                          draggableId={task.task_id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-2xl p-4 shadow-sm border border-slate-100 transition-all ${
                                snapshot.isDragging ? "shadow-xl rotate-1" : "hover:shadow-md"
                              }`}
                              data-testid={`task-${task.task_id}`}
                            >
                              <div className="flex items-start gap-2 mb-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="p-1 hover:bg-slate-100 rounded cursor-grab active:cursor-grabbing flex-shrink-0"
                                >
                                  <GripVertical size={16} className="text-slate-400" />
                                </div>
                                <h4 className="font-medium text-slate-800 flex-1 line-clamp-2">
                                  {task.title}
                                </h4>
                                {isSuperAdmin && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      onClick={() => handleEditTask(task)}
                                      className="p-1 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                                      title="Editar"
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task.task_id)}
                                      className="p-1 hover:bg-rose-50 hover:text-rose-600 rounded-lg transition-colors"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {task.description && (
                                <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                                  {task.description}
                                </p>
                              )}

                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`badge text-xs ${
                                    priorities[task.priority]?.color
                                  }`}
                                >
                                  {priorities[task.priority]?.label}
                                </span>
                                {task.tags?.slice(0, 2).map((tag, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-slate-100 text-slate-600 text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>

                              {task.due_date && (
                                <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                                  <Calendar size={14} />
                                  <span>
                                    {new Date(task.due_date).toLocaleDateString("es-ES", {
                                      day: "numeric",
                                      month: "short",
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </div>
                    {provided.placeholder}
                    
                    {getTasksByStatus(column.id).length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <p className="text-sm">Sin tareas en esta columna</p>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Create/Edit Task Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg animate-slide-up" data-testid="create-task-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingTask ? "Editar Tarea" : "Nueva Tarea"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTask(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) =>
                    setNewTask({ ...newTask, title: e.target.value })
                  }
                  className="w-full glass-input"
                  placeholder="Nombre de la tarea"
                  data-testid="task-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) =>
                    setNewTask({ ...newTask, description: e.target.value })
                  }
                  className="w-full glass-input min-h-[100px]"
                  placeholder="Descripción de la tarea"
                  data-testid="task-description-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Prioridad
                  </label>
                  <select
                    value={newTask.priority}
                    onChange={(e) =>
                      setNewTask({ ...newTask, priority: e.target.value })
                    }
                    className="w-full glass-input"
                    data-testid="task-priority-select"
                  >
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha límite
                  </label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) =>
                      setNewTask({ ...newTask, due_date: e.target.value })
                    }
                    className="w-full glass-input"
                    data-testid="task-date-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Columna
                </label>
                <select
                  value={newTask.status}
                  onChange={(e) =>
                    setNewTask({ ...newTask, status: e.target.value })
                  }
                  className="w-full glass-input"
                  data-testid="task-status-select"
                >
                  {columns.map((col) => (
                    <option key={col.id} value={col.id}>{col.title}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingTask(null);
                  }}
                  className="btn-secondary order-2 sm:order-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingTask ? handleUpdateTask : handleCreateTask}
                  disabled={!newTask.title.trim()}
                  className="btn-primary order-1 sm:order-2"
                  data-testid="save-task-btn"
                >
                  {editingTask ? "Guardar Cambios" : "Crear Tarea"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Operaciones;
